import { differenceInDays, startOfDay, endOfDay, isBefore, isEqual, addDays } from 'owa-datetime';
import { DateRange } from 'owa-datetime-utils';
import {
    getScenarioListCacheId,
    getSearchText,
    shouldShowDeclinedEvents,
} from '../selectors/agendaStoreSelectors';
import {
    getEvents as getEventsFunction,
    getLoadedDateRange as getLoadedDateRangeFunction,
    ScenarioLoadState,
    getLoadState as getLoadStateFunction,
} from 'owa-calendar-events-loader';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { containsText } from 'owa-calendar-event-capabilities';
import { FailureType } from 'owa-calendar-load-error-message';
import { getFailureState as getFailureStateFunction } from 'owa-agenda-events-common';

export function getCanLoadMore(maxDays: number, scenarioId: string): boolean {
    const loadedDateRange = getLoadedDateRange(scenarioId);
    if (!loadedDateRange) {
        return false;
    }
    const hasLoadedMaxDays = maxDays
        ? differenceInDays(loadedDateRange.end, loadedDateRange.start) < maxDays
        : true;
    return hasLoadedMaxDays;
}

export function getLoadedDayDateRanges(scenarioId: string): DateRange[] {
    const fullLoadedDateRange = getLoadedDateRange(scenarioId);

    if (!fullLoadedDateRange) {
        return [];
    }
    let startDate = startOfDay(fullLoadedDateRange.start);
    const dateRanges = [];
    while (
        isBefore(endOfDay(startDate), fullLoadedDateRange.end) ||
        isEqual(endOfDay(startDate), fullLoadedDateRange.end)
    ) {
        let dateRange = {
            start: startDate,
            end: endOfDay(startDate),
        };
        dateRanges.push(dateRange);
        startDate = addDays(startDate, 1);
    }
    return dateRanges;
}

export function getEvents(scenarioId: string, dateRange: DateRange): CalendarEvent[] {
    const listCacheId = getScenarioListCacheId(scenarioId);
    const searchText = getSearchText(scenarioId);
    const shouldFilterOutDeclinedEvents = !shouldShowDeclinedEvents(scenarioId);
    const filterEvents = (event: CalendarEvent) => {
        if (shouldFilterOutDeclinedEvents && event.ResponseType === 'Decline') {
            return false;
        }
        return containsText(event, searchText);
    };
    return getEventsFunction(listCacheId, dateRange).filter(filterEvents);
}

export function getLoadedDateRange(scenarioId: string): DateRange | null {
    const listCacheId = getScenarioListCacheId(scenarioId);
    return getLoadedDateRangeFunction(listCacheId);
}

export function getLoadState(scenarioId: string): ScenarioLoadState {
    const listCacheId = getScenarioListCacheId(scenarioId);
    return getLoadStateFunction(listCacheId);
}

export function getFailureState(scenarioId: string): FailureType | null {
    const listCacheId = getScenarioListCacheId(scenarioId);
    return getFailureStateFunction(listCacheId);
}
